import { CheckBadgeIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useMemo, useRef } from 'react';
import { useHoverDirty } from 'react-use';
import { ListingNoteBadge } from 'src/components/Listings/ListingNoteBadge/index';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { shared, vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { HoverableIcon } from 'src/core/ui/HoverableIcon/HoverableIcon';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ListingIcon, UnbroadcastIcon } from 'src/svgs';
import { ChainLinkIcon } from 'src/svgs/ChainLinkIcon';
import { GenericDisclosuresIcon, InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, ListingNoteType } from 'src/WebApiController';

import { AutoPriceResultStateBadge } from '../AutoPriceResultStateBadge';
import { ComparableListingDisplayProps } from './ComparableListingDisplay';
import * as styles from './ComparableListingDisplay.css';
import { ComparableListingDisplayTooltip } from './ComparableListingDisplayTooltip';
import { ComparableListingSeatingDisplay } from './ComparableListingSeatingDisplay';

type ComparableListingSeatingProps = { shouldGrayOut?: boolean } & Pick<
  ComparableListingDisplayProps,
  | 'listingId'
  | 'title'
  | 'seating'
  | 'isYourListing'
  | 'autoPricingResultType'
  | 'autoPricingResultConstraintType'
  | 'shouldShowResultTypeAction'
>;
export const ComparableListingSeating = ({
  listingId,
  title,
  seating,
  isYourListing = false,
  shouldGrayOut = false,
  autoPricingResultType,
  autoPricingResultConstraintType,
  shouldShowResultTypeAction,
}: ComparableListingSeatingProps) => {
  return (
    <Stack direction="row" gap="s">
      {isYourListing && (
        <AutoPriceResultStateBadge
          listingId={listingId}
          autoPricingResultType={autoPricingResultType}
          autoPricingResultConstraintType={autoPricingResultConstraintType}
          shouldShowResultTypeAction={shouldShowResultTypeAction}
        />
      )}

      <span
        className={clsx(shared.typography.subtitle1, styles.seatingWrapper, {
          [styles.grayedTile]: shouldGrayOut,
        })}
      >
        {title ?? (
          <ComparableListingSeatingDisplay
            section={seating?.section ?? ''}
            row={seating?.row ?? ''}
            isYourListing={isYourListing}
          />
        )}
      </span>
    </Stack>
  );
};

type ComparableListingStatesProps = {
  shouldGrayOut?: boolean;
  isPosListing?: boolean;
} & Pick<
  ComparableListingDisplayProps,
  | 'listingId'
  | 'isYourListing'
  | 'isListed'
  | 'listingNotes'
  | 'isFanInventory'
  | 'validPurchaseQuantities'
>;
export const ComparableListingStates = ({
  listingId,
  isListed = false,
  isYourListing = false,
  isPosListing = false,
  shouldGrayOut = false,
  listingNotes,
  isFanInventory,
  validPurchaseQuantities,
}: ComparableListingStatesProps) => {
  const { isDarkMode } = useSiteTheme();
  const hasYourListingInComps = useUserHasFeature(Feature.YourListingsInComps);
  const hasShowIdFeature = useUserHasFeature(Feature.ListingIdForCompListings);

  const listingNoteDefects = useMemo(() => {
    return listingNotes
      ?.filter(
        (note) =>
          note.type === ListingNoteType.Defect ||
          note.type === ListingNoteType.RestrictionOnUse
      )
      .map((note) => note.listingNoteText);
  }, [listingNotes]);

  const listingNotePerks = useMemo(() => {
    return listingNotes
      ?.filter(
        (note) =>
          note.type === ListingNoteType.Perk ||
          note.type === ListingNoteType.Information
      )
      .map((note) => note.listingNoteText);
  }, [listingNotes]);

  const shouldShowVerifiedBadge = useMemo(() => {
    return !isFanInventory && !isYourListing;
  }, [isFanInventory, isYourListing]);

  const displayQuantity = useMemo(() => {
    if (!validPurchaseQuantities) {
      return 0;
    }

    return Math.max(...validPurchaseQuantities);
  }, [validPurchaseQuantities]);

  const quantityBadge = (
    <span className={styles.quantityText}>
      <Content id={ContentId.QuantityAbbreviated} />: {displayQuantity}
    </span>
  );
  const quantitySplits = useMemo(() => {
    if (!validPurchaseQuantities || validPurchaseQuantities.length === 0) {
      return '0';
    }

    const splits = [...validPurchaseQuantities]
      .toSorted((a, b) => b - a)
      .filter((q) => q < 10);

    const hasSplitsLargerThanTen = validPurchaseQuantities.some((q) => q > 10);

    return [...(hasSplitsLargerThanTen ? ['10+'] : []), ...splits].join(', ');
  }, [validPurchaseQuantities]);

  return (
    <Stack
      className={clsx(styles.splitInfo, {
        [styles.grayedTile]: shouldGrayOut,
      })}
      direction="row"
      alignItems="center"
      gap="s"
    >
      {hasYourListingInComps && isPosListing && (
        <UnbroadcastBadge listed={isListed} />
      )}
      {shouldShowVerifiedBadge && (
        <div className={styles.exlcToInclFanInvIconContainer}>
          <CheckBadgeIcon
            className={styles.exlcToInclFanInvIcon}
            stroke={isDarkMode ? 'white' : 'black'}
            strokeWidth={1}
          />
        </div>
      )}

      <Tooltip
        triggerContent={quantityBadge}
        contentVariant={isDarkMode ? 'light' : 'darkGray'}
      >
        <Content id={ContentId.Splits} />: {quantitySplits}
      </Tooltip>

      {listingNoteDefects?.length ? (
        <ListingNoteBadge
          icon={<InfoOutlineIcon />}
          listingNotes={listingNoteDefects}
          type={ListingNoteType.Defect}
        />
      ) : null}

      {listingNotePerks?.length ? (
        <ListingNoteBadge
          icon={<GenericDisclosuresIcon />}
          listingNotes={listingNotePerks}
          type={ListingNoteType.Perk}
        />
      ) : null}

      {hasShowIdFeature && (
        <div
          className={clsx(styles.listingIdBadge, {
            [styles.grayedTile]: shouldGrayOut,
          })}
        >
          {listingId}
        </div>
      )}
    </Stack>
  );
};

type ComparableListingBadgeProps = {
  isPosListing?: boolean;
} & Pick<
  ComparableListingDisplayProps,
  | 'listingId'
  | 'seating'
  | 'price'
  | 'unitCost'
  | 'listingPriceCalc'
  | 'listingPriceCalculations'
  | 'comparableListings'
  | 'isExcludingFanInventory'
  | 'isYourListing'
  | 'isAnchor'
  | 'isOutlier'
  | 'isSelectedWithListingGroup'
>;
export const ComparableListingBadge = ({
  listingId,
  seating,
  price,
  unitCost,
  listingPriceCalc,
  listingPriceCalculations,
  comparableListings,
  isAnchor,
  isOutlier,
  isYourListing,
  isPosListing,
  isSelectedWithListingGroup,
  isExcludingFanInventory,
}: ComparableListingBadgeProps) => {
  const hasYourListingInComps = useUserHasFeature(Feature.YourListingsInComps);
  const myListingText = useContent(ContentId.MyListings);
  const myListingTextTrunc = useMemo(() => {
    return myListingText.slice(0, -1);
  }, [myListingText]);

  if (isYourListing) {
    if (isSelectedWithListingGroup) {
      return <div className={styles.myListingBadge}>{myListingTextTrunc}</div>;
    }

    return (
      <div className={styles.myListingBadge}>
        <ComparableListingDisplayTooltip
          listingId={listingId}
          seating={seating}
          price={price}
          unitCost={unitCost}
          listingPriceCalc={listingPriceCalc}
          listingPriceCalculations={listingPriceCalculations}
          comparableListings={comparableListings}
          includeFanInventory={isExcludingFanInventory}
        />
      </div>
    );
  }

  if (isPosListing && hasYourListingInComps) {
    return (
      <div className={styles.referenceListingBadge}>
        <span className={styles.badgeTextContainer}>{myListingTextTrunc}</span>
        <span className={styles.referenceIconContainer}>
          <HoverableIcon
            icon={<ListingIcon size={vars.iconSize.s} />}
            tooltip={myListingTextTrunc}
          />
        </span>
      </div>
    );
  }

  if (isOutlier) {
    return (
      <div className={styles.outlierBadge}>
        <span className={styles.badgeTextContainer}>
          <Content id={ContentId.Outlier} />
        </span>
        <span className={styles.outlierBadgeIconContainer}>
          <NoSymbolIcon className={styles.badgeIcon} strokeWidth={2} />
        </span>
      </div>
    );
  }

  if (isAnchor) {
    return (
      <div className={styles.referenceListingBadge}>
        <span className={styles.badgeTextContainer}>
          <Content id={ContentId.Reference} />
        </span>
        <span className={styles.referenceIconContainer}>
          <HoverableIcon
            icon={<ChainLinkIcon size={vars.iconSize.s} />}
            tooltip={<Content id={ContentId.Reference} />}
          />
        </span>
      </div>
    );
  }

  return null;
};

const UnbroadcastBadge = ({ listed }: { listed: boolean }) => {
  const { isDarkMode } = useSiteTheme();
  const ref = useRef<HTMLDivElement>(null);
  const isHovering = useHoverDirty(ref);

  const strokeColor = useMemo(() => {
    if (listed) {
      return isDarkMode ? 'white' : 'black';
    }
    return vars.color.textDisabled;
  }, [isDarkMode, listed]);

  return (
    <Tooltip
      open={isHovering}
      triggerContent={
        <div ref={ref}>
          <UnbroadcastIcon
            noOutline
            className={styles.exlcToInclFanInvIcon}
            stroke={strokeColor}
            strokeWidth={1}
          />
        </div>
      }
      contentVariant={isDarkMode ? 'light' : 'darkGray'}
    >
      <Content id={listed ? ContentId.Broadcast : ContentId.Unbroadcast} />
    </Tooltip>
  );
};

type ComparableListingPriceProps = {
  shouldGrayOut?: boolean;
  isPosListing?: boolean;
} & Pick<ComparableListingDisplayProps, 'price' | 'unitCost' | 'isYourListing'>;
export const ComparableListingPrice = ({
  price,
  unitCost,
  isYourListing = false,
  isPosListing = false,
  shouldGrayOut = false,
}: ComparableListingPriceProps) => {
  const { isDarkMode } = useSiteTheme();
  return (
    <Stack
      className={styles.right}
      alignItems="center"
      justifyContent="center"
      gap="s"
      direction="column"
    >
      <span
        className={clsx(styles.priceText, {
          [styles.highlightedPrice]: isPosListing,
          [styles.highlightedPriceDark]: isPosListing && isDarkMode,
          [styles.darkPriceText]: isDarkMode && !isPosListing,
          [styles.grayedOutPriceText]: shouldGrayOut,
        })}
      >
        {price}
      </span>
      {isYourListing && unitCost && (
        <span className={styles.unitCostText}>({unitCost})</span>
      )}
    </Stack>
  );
};
